import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction, listSearchSchema } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { bankAccountRequestFilters } from './bank-account-request.filters';

// Manifesto da lista (U23/U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K. O lote (aprovar,
// reprovar, instrumento) é da tela, com diálogo; o agente só abre, pagina, filtra e exclui por linha.

export const BANK_ACCOUNT_REQUEST_LIST_PATH = '/contas-digitais/solicitacoes';

/** `validateSearch` da lista (U07): página, tamanho e os filtros legíveis (P1). */
export const bankAccountRequestListSearch = listSearchSchema({ ...bankAccountRequestFilters.shape });

export const bankAccountRequestOpen = action<{ id: string }>({
  id: 'bankAccountRequest.open',
  kind: 'navigate',
  row: true,
  page: true,
  label: msg`Abrir solicitação`,
  description: msg`Abre o detalhe de uma solicitação de conta pelo id.`,
  permission: can('BankAccountRequest', 'Read'),
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(`${BANK_ACCOUNT_REQUEST_LIST_PATH}/${encodeURIComponent(input.id)}`),
});

export const bankAccountRequestCreate = action({
  id: 'bankAccountRequest.create',
  kind: 'navigate',
  label: msg`Novo cadastro`,
  description: msg`Abre o formulário em branco de nova solicitação de conta.`,
  permission: can('BankAccountRequest', 'Create'),
  run: ({ nav }) => nav.to(`${BANK_ACCOUNT_REQUEST_LIST_PATH}/nova`),
});

export const bankAccountRequestListPage = listPageAction('bankAccountRequest.list.page');

const filterKeys = bankAccountRequestFilters.keys.join(', ');

export const bankAccountRequestFilter = filterAction('bankAccountRequest.filter', bankAccountRequestFilters, {
  description: msg`Filtra a lista de solicitações de conta. Filtro omitido é limpo; datas são civis (AAAA-MM-DD); documento e telefone só com dígitos. Chaves: ${filterKeys}.`,
});

/* eslint-disable lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33) */
export const NOT_IN_LIST = 'This request is not on the list page open on screen. Nothing was deleted; open the page that shows it first.';
/* eslint-enable lingui/no-unlocalized-strings */

/**
 * Excluir pela lixeira da linha (BankAccountRequestList.tsx:218-226): intenção do bloco, só humana, com a
 * confirmação da tela. Não entra no manifesto: o agente exclui no detalhe, preso à solicitação aberta (B1).
 */
export const bankAccountRequestDelete = action<{ id: string }>({
  id: 'bankAccountRequest.list.delete',
  kind: 'destructive',
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui uma solicitação de conta da página da lista aberta na tela, pela linha (só a pessoa, pela lixeira da linha).`,
  permission: can('BankAccountRequest', 'Delete'),
  input: z.object({ id: z.string().min(1) }),
  preview: () => ({ title: i18n._(msg`Excluir`), detail: i18n._(msg`Tem certeza que deseja excluir essa conta?`), tone: 'destructive' }),
  run: () => {
    throw new Error(NOT_IN_LIST);
  },
});

export const bankAccountRequestListScreen = defineScreen({
  id: 'bankAccountRequest.list',
  path: BANK_ACCOUNT_REQUEST_LIST_PATH,
  title: msg`Solicitações`,
  description: msg`Lista de solicitações de abertura de conta, com filtros (documento, email, parceiro, solicitante, número da conta, telefone, status, tipo de conta, data de início), paginação e aprovação ou reprovação em lote, inclusive do instrumento, para linhas no mesmo status.`,
  permission: can('BankAccountRequest', 'Read'),
  place: { icon: 'landmark', section: 'bank-account' },
  actions: [bankAccountRequestOpen, bankAccountRequestCreate, bankAccountRequestListPage, bankAccountRequestFilter],
});

/** O que o agente lê da lista (U24, U46 D2-C): no idioma da tela, contagem e filtros ativos (só as chaves), sem dado pessoal. */
export function bankAccountRequestListContext(input: { readonly total: number | undefined; readonly filters: readonly string[]; readonly selected: number }): ScreenContext {
  const total = input.total;
  const filterNames = input.filters.join(', ');
  const selected = input.selected;
  return {
    summary: [
      t`Lista de solicitações de conta.`,
      total === undefined ? t`Carregando.` : t`${total} solicitações.`,
      input.filters.length > 0 ? t`Filtros: ${filterNames}.` : t`Sem filtros.`,
      t`${selected} selecionadas.`,
    ].join(' '),
    fields: { total: input.total ?? null, selected: input.selected },
    sensitivity: 'internal',
  };
}
